import { ThumbnailMetrics } from 'Roblox';
import { WebGLJson } from 'THREE';
import { AxiosPromise, httpService } from 'core-utilities';
import { metricsService } from 'core-roblox-utilities';

import {
  Thumbnail3DJsonSuccess,
  Thumbnail3DJsonFail,
  Thumbnail3DJsonResponse,
  Thumbnail3DJsonStats,
  Thumbnail3DJsonUrlResponse,
  DefaultMaxRetry,
  DefaultMaxRetryInterval,
  FeatureName,
  LoadFailureName,
  LoadSuccessMetricsType,
  LoadSuccessName,
  LoadFailureMetricsType,
  ThumbnailStates
} from '../constants/thumbnail3dConstant';

import { getAvatarThumbnail3dJsonUrl } from '../constants/urlConstant';
import thumbnailRenderer from '../util/thumbnailRenderer';

const { loadObjAndMtl3D } = thumbnailRenderer;

const get3DJsonData = (
  jsonRes: Thumbnail3DJsonResponse,
  success: Thumbnail3DJsonSuccess,
  fail: Thumbnail3DJsonFail,
  stats: Thumbnail3DJsonStats
) => {
  const jsonUrlConfig = {
    url: jsonRes.data.imageUrl
  };
  httpService
    .get(jsonUrlConfig)
    .then((jsonUrlRes: Thumbnail3DJsonUrlResponse) => {
      success(jsonUrlRes.data, stats);
    })
    .catch(() => {
      fail('3D Thumbnail failed to load');
    });
};

const get3DJson = (
  targetId: number,
  getJson: () => AxiosPromise<unknown>,
  success: Thumbnail3DJsonSuccess,
  fail: Thumbnail3DJsonFail,
  retries: number,
  retryInterval: number,
  stats: Thumbnail3DJsonStats
) => {
  let requestJson;
  if (getJson === null || getJson === undefined) {
    const urlConfig = { url: getAvatarThumbnail3dJsonUrl(targetId), withCredentials: true };
    requestJson = httpService.get(urlConfig);
  } else {
    requestJson = getJson();
  }

  requestJson
    .then((jsonRes: Thumbnail3DJsonResponse) => {
      if (jsonRes.data && jsonRes.data.state === ThumbnailStates.complete) {
        get3DJsonData(jsonRes, success, fail, stats);
      } else {
        stats.realRegeneration = false;
        if (retries-- > 0) {
          stats.retriesDone++;
          setTimeout(() => {
            get3DJson(targetId, getJson, success, fail, retries, retryInterval, stats);
          }, retryInterval);
        } else {
          fail('3D Thumbnail failed to load');
        }
      }
    })
    .catch(() => {
      fail('3D Thumbnail failed to load');
    });
};

const processRequest = (
  targetId: number,
  getJson: () => AxiosPromise<unknown>,
  success: Thumbnail3DJsonSuccess,
  fail: Thumbnail3DJsonFail,
  retries: number,
  retryInterval: number
) => {
  const stats = {
    realRegeneration: false,
    startTime: new Date(),
    retriesDone: 0
  };
  get3DJson(targetId, getJson, success, fail, retries, retryInterval, stats);
};

const getThumbnailJson = (
  targetId = 0,
  getJson: () => AxiosPromise<unknown>,
  retries: number = DefaultMaxRetry,
  retryInterval: number = DefaultMaxRetryInterval
) => {
  if (!targetId && getJson !== null && getJson !== undefined) {
    return new Promise((resolve, reject) => {
      reject('TargetId or GetJson function can not be empty.');
    });
  }

  return new Promise((resolve, reject) => {
    processRequest(
      targetId,
      getJson,
      (json: WebGLJson, stats: Thumbnail3DJsonStats) => {
        const finishTime = new Date().getTime();
        const duration = finishTime - stats.startTime.getTime();
        if (ThumbnailMetrics) {
          ThumbnailMetrics.logFinalThumbnailTime(duration);
        }

        if (metricsService) {
          metricsService
            .logMeasurement({
              featureName: FeatureName,
              measureName: LoadSuccessName,
              metricsType: LoadSuccessMetricsType,
              excludeCountry: true,
              value: duration
            })
            .catch(console.debug);
        }
        resolve({ json, performance: { duration } });
      },
      (error: string) => {
        if (ThumbnailMetrics) {
          ThumbnailMetrics.logThumbnailTimeout();
        }

        if (metricsService) {
          metricsService
            .logMeasurement({
              featureName: FeatureName,
              measureName: LoadFailureName,
              metricsType: LoadFailureMetricsType,
              excludeCountry: true
            })
            .catch(console.debug);
        }

        reject(error);
      },
      retries,
      retryInterval
    );
  });
};

export default {
  getThumbnailJson,
  loadObjAndMtl3D
};
